import { and, eq, not, or } from 'tio-ui/utilities';
import Button from 'tio-reimagined/components/button';
import { concat, hash } from '@ember/helper';
import Checkbox from 'tio-reimagined/components/checkbox';
import Popover from 'tio-reimagined/components/popover';
import { dropTask } from 'ember-concurrency';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, Section } from 'tio-reimagined/components/layout';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import formatCentsToDollars from '../../../helpers/format-cents-to-dollars.ts';
import inputmask from '../../../modifiers/inputmask.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import SelectableCard from 'tio-ui/components/selectable-card';
import Table from 'tio-reimagined/components/table';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import type {
  AppHistoryBase,
  PaymentEvent,
  AssistanceAppHistory,
} from '../../../types/tuition-assistance.ts';
import type Intl from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import ReimbursementCalculationIfApplicable from '../../tuition-assistance/reimbursement-calculation-if-applicable.gts';
import {
  PhDotsThreeVertical,
  PhGift,
  PhNotePencil,
  PhShieldCheck,
  PhTrash,
} from 'ember-phosphor-icons';
import { fn } from '@ember/helper';
import { updateTasAssistanceAmountRequest } from '../../../requests/tas-assistance.ts';

interface RequestSummaryAndApprovedTotalSig {
  Args: {
    application: TasApplicationModel;
    isProhibitedEdit?: boolean;
  };
  Element: HTMLElement;
}

const actionMap: Record<string, string> = {
  requestRepayment: 'requestRepayment',
  payMore: 'requestPayment',
  editApprovedTotal: 'updateApprovedTotal',
  forgive: 'forgiveRepayment',
  settle: 'recordRepayment',
  delete: 'cancelRepayment',
};

export default class RequestSummaryComponent extends Component<RequestSummaryAndApprovedTotalSig> {
  @tracked submitError = null;
  @tracked isAddTransactionOpen = false;
  @tracked reasonForNewTransaction = '';
  @tracked newTransactionAmount = 0;
  @tracked newTransactionType = '';
  @tracked didSubmitNewRequest = false;
  @tracked pauseEmployee = false;

  @service declare store: StoreService;
  // @service declare notifications: NotificationsService;
  @service declare intl: Intl;
  @service declare tuitionAssistance: TuitionAssistanceService;

  get adapterMethod() {
    return actionMap[this.newTransactionType] || '';
  }

  get isCourseCashApplication() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get showRepaymentButton() {
    return (
      this.args.application.canRequestRepayment &&
      ['TAS.ApplicationState.FULFILLED', 'TAS.ApplicationState.EVIDENCE_REJECTED'].includes(
        this.args.application.state
      )
    );
  }

  canEditAssistance = (
    assistance: AppHistoryBase | PaymentEvent | AssistanceAppHistory,
    index: number,
    logType?: string
  ): boolean => {
    if (this.args.isProhibitedEdit) {
      return false;
    }
    if (logType === 'approval') {
      const amounts = this.args.application.amounts;
      return (
        !amounts.flags.request_payment_enabled &&
        !amounts.flags.request_repayment_enabled &&
        amounts.pending_payment === 0 &&
        amounts.paid === 0 &&
        index === this.lastApprovedTotalHistoryIndex
      );
    } else {
      if (!('records' in assistance) || !('state' in assistance)) {
        return false;
      }
      const assistanceLength = (assistance.records?.length ?? 0) - 1;
      // Check if this is the last record AND check the record's 'to' state
      if (index === assistanceLength && assistance.records && assistance.records[index]) {
        const recordToState = assistance.records[index].to;
        return (
          recordToState === 'TAS.AssistanceState.PAYABLE' ||
          recordToState === 'TAS.AssistanceState.REPAYABLE' ||
          recordToState === 'TAS.AssistanceState.RESERVED'
        );
      }
      return false;
    }
  };

  setTransactionType = (value: string) => {
    this.newTransactionType = value;

    //  if asking for repayment, set editableAmount to the absolute value of paidTotal
    if (value === 'requestRepayment') {
      this.newTransactionAmount = Math.abs(this.args.application.amounts.refund_limit) || 0;
      this.pauseEmployee = true;
    } else {
      this.newTransactionAmount = 0;
      this.pauseEmployee = false;
    }
  };

  updateReasonForNewTransaction = (reason: string) => {
    this.reasonForNewTransaction = reason;
  };

  updateNewAmount = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event);
    const amount = Math.round(Number(unmaskedValue) * 100);
    this.newTransactionAmount = amount;
  };

  get tasParticipant() {
    return this.args.application.tasProgramInstance.employee.tasParticipant;
  }

  submitNewTransactionTask = dropTask(async () => {
    this.submitError = null;
    this.didSubmitNewRequest = true;
    const adapter = this.store.adapterFor('tas-application');
    const shouldPauseEmployee =
      this.newTransactionType === 'requestRepayment' && this.pauseEmployee;
    const participantId = this.tasParticipant.id;

    try {
      // @ts-expect-error - Legacy adapter method, will be migrated
      await adapter[this.adapterMethod](
        this.args.application,
        this.newTransactionAmount,
        this.reasonForNewTransaction
      );

      if (shouldPauseEmployee) {
        // @ts-expect-error - Legacy adapter method, will be migrated
        await this.store.adapterFor('tas-participant').holdTasParticipant(participantId);
      }

      this.closeAddTransactionModal();
      this.didSubmitNewRequest = false;
      await this.args.application.loadHistory();
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  resetForm() {
    this.newTransactionType = '';
    this.reasonForNewTransaction = '';
    this.newTransactionAmount = 0;
    this.pauseEmployee = false;
    this.submitError = null;
  }

  openAddTransactionModal = () => {
    this.isAddTransactionOpen = true;
  };

  closeAddTransactionModal = () => {
    this.isAddTransactionOpen = false;
    this.resetForm();
  };

  get isFormValid() {
    return this.reasonForNewTransaction && this.newTransactionAmount > 0 && this.newTransactionType;
  }

  togglePauseEmployee = async () => {
    this.pauseEmployee = !this.pauseEmployee;
  };

  get shouldValidateAmountBasedOnLimits() {
    return this.newTransactionType !== 'payMore';
  }

  get lastApprovedTotalHistoryIndex(): number {
    return this.args.application.approvedTotalHistory.length - 1;
  }

  get filteredAssistanceHistory() {
    // Backend now handles COURSE_CASH group merging and sorting.
    // Frontend only needs to filter out CARD_AUTHORIZED when CARD_CAPTURED exists
    // (to avoid showing both in the UI since CAPTURED is the final state).
    return this.args.application.assistanceHistory.map((assistance) => {
      const hasCaptured = assistance.records?.some(
        (record) => record.assistanceType === 'CARD_CAPTURED'
      );

      // If CARD_CAPTURED exists, filter out CARD_AUTHORIZED
      const filteredRecords = hasCaptured
        ? assistance.records?.filter((record) => record.assistanceType !== 'CARD_AUTHORIZED')
        : assistance.records;

      return {
        ...assistance,
        records: filteredRecords,
      };
    });
  }

  <template>
    <VStack class="w-full">
      <Section>
        <:header>
          {{t "common.tas.approval.request_summary.default"}}
        </:header>
        <:actions>
          <Button
            @intent="primary"
            title={{t "common.tas.approval.request_summary.request_refund_description"}}
            {{on "click" this.openAddTransactionModal}}
            data-test="request-change"
          >
            {{t "common.tas.approval.request_summary.make_payment_changes"}}
          </Button>
        </:actions>
        <:body>
          <Table>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t "common.tas.approval.request_summary.event_type"}}</options.th>
                <options.th>{{t "common.tas.approval.request_summary.amount"}}</options.th>
                <options.th>{{t "common.tas.approval.request_summary.date"}}</options.th>
                <options.th>{{t "common.tas.approval.request_summary.action_taken_by"}}</options.th>
                <options.th />
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each @application.approvedTotalHistory as |logItem index|}}
                <options.tr>
                  <options.td>
                    {{#if (eq index 0)}}
                      {{#if this.isCourseCashApplication}}
                        {{t "common.tas.approval.request_summary.approved_reimbursement_total"}}
                      {{else}}
                        {{t "common.tas.approval.request_summary.approved_total"}}
                      {{/if}}
                    {{else}}
                      {{#if this.isCourseCashApplication}}
                        {{t
                          "common.tas.approval.request_summary.approved_reimbursement_total_updated"
                        }}
                      {{else}}
                        {{t "common.tas.approval.request_summary.approved_total_updated"}}
                      {{/if}}
                    {{/if}}
                  </options.td>
                  <options.td>{{formatCentsToDollars (or logItem.amount logItem.to)}}</options.td>
                  <options.td>
                    {{safeFormatDate
                      logItem.date
                      month="short"
                      day="2-digit"
                      year="numeric"
                      hour="numeric"
                      minute="2-digit"
                    }}
                  </options.td>
                  <options.td>{{logItem.approverName}}</options.td>
                  {{#if (this.canEditAssistance logItem index logItem.logType)}}
                    <options.td>
                      <EditPaymentDropdown
                        @application={{@application}}
                        @record={{logItem}}
                        @logType="approval"
                        @tasParticipantActive={{this.tasParticipant.isActive}}
                      />
                    </options.td>
                  {{else}}
                    <options.td />
                  {{/if}}
                </options.tr>
              {{/each}}

              {{#each this.filteredAssistanceHistory as |assistance|}}
                {{#each assistance.records as |record index|}}
                  <options.tr>
                    <options.td>{{record.assistanceType}}</options.td>
                    <options.td>{{formatCentsToDollars record.amount}}</options.td>
                    <options.td>
                      {{safeFormatDate
                        record.date
                        month="short"
                        day="2-digit"
                        year="numeric"
                        hour="numeric"
                        minute="2-digit"
                      }}
                    </options.td>
                    <options.td>{{record.approverName}}</options.td>
                    {{#if (this.canEditAssistance assistance index)}}
                      <options.td>
                        <EditPaymentDropdown
                          @application={{@application}}
                          @assistanceId={{assistance.assistance_id}}
                          @record={{record}}
                          @logType="assistance"
                          @tasParticipantActive={{this.tasParticipant.isActive}}
                        />
                      </options.td>
                    {{else}}
                      <options.td />
                    {{/if}}
                  </options.tr>
                {{/each}}
              {{/each}}
            </:tbody>
          </Table>
        </:body>
      </Section>
    </VStack>

    <LimitsSummary
      @application={{@application}}
      @employee={{@application.tasProgramInstance.employee}}
      @approvedTotalToValidate={{this.newTransactionAmount}}
      @ignoreLimitsValidation={{this.shouldValidateAmountBasedOnLimits}}
      @labelClass="w-full"
      @valueClass="w-full app-status-text-brand"
      as |Summary|
    >
      <Modal
        @isOpen={{this.isAddTransactionOpen}}
        @onClose={{this.closeAddTransactionModal}}
        data-test-edit-modal
        @ariaLabel={{t "common.aria_labels.request_summary"}}
      >
        <:header>{{t "common.tas.approval.request_summary.default"}}</:header>
        <:body>
          <div>{{t "common.tas.approval.request_summary.select_action"}}</div>
          <div class="flex gap-4 mt-4 mb-8 text-pretty">
            <SelectableCard
              @value="requestRepayment"
              @label="Request Repayment"
              @description="The employee must return previously paid funds"
              @isSelected={{eq this.newTransactionType "requestRepayment"}}
              @onChange={{this.setTransactionType}}
              @disabled={{not @application.amounts.flags.request_repayment_enabled}}
            />
            <SelectableCard
              @value="payMore"
              @label={{t "common.tas.approval.request_summary.pay_more_label"}}
              @description="The employee is being given extra funds"
              @isSelected={{eq this.newTransactionType "payMore"}}
              @onChange={{this.setTransactionType}}
              @disabled={{not @application.amounts.flags.request_payment_enabled}}
            />
          </div>
          <form class="flex flex-col">
            <Input
              @label={{concat "Amount" "*"}}
              data-test-amount-update
              @value={{String (divide this.newTransactionAmount 100)}}
              @onChange={{this.updateNewAmount}}
              @classes={{hash base="text-xs font-semibold"}}
              placeholder="$00.00"
              {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
            />
            <div class="mb-8">
              <p class="text-xs pt-1 px-2 text-tio-gray-400">
                {{#if (eq this.newTransactionType "payMore")}}
                  {{t "common.tas.approval.request_summary.additional_amount_paid_to"}}
                {{else if (eq this.newTransactionType "requestRepayment")}}
                  {{t "common.tas.approval.request_summary.amount_to_request_repayment"}}
                {{/if}}
              </p>
              <Summary.LimitsAlertForApproverIfApplicable class="text-sm" />
            </div>

            <Textarea
              data-test-reason-for-edit
              @value={{this.reasonForNewTransaction}}
              @onChange={{this.updateReasonForNewTransaction}}
              @label={{concat "Specify Reason" "*"}}
              @classes={{hash base="text-xs font-semibold"}}
              placeholder="Add a comment"
            />
            {{#if (eq this.newTransactionType "requestRepayment")}}
              <Checkbox
                @label={{t "common.tas.approval.request_summary.place_employee_hold"}}
                @checked={{this.pauseEmployee}}
                @onChange={{this.togglePauseEmployee}}
                class="text-sm font-semibold mt-4 flex items-center"
              />
            {{/if}}
            <ErrorMessages @showErrors={{this.didSubmitNewRequest}} @error={{this.submitError}} />
          </form>
        </:body>
        <:footer>
          <Footer
            @isRunning={{this.submitNewTransactionTask.isRunning}}
            @onCancel={{this.closeAddTransactionModal}}
            @onSubmit={{this.submitNewTransactionTask.perform}}
            @submitDisabled={{or (not this.isFormValid) Summary.isDisabledSubmitForApprover}}
          />
        </:footer>
      </Modal>
    </LimitsSummary>
  </template>
}

interface EditPaymentDropDownSignature {
  Args: {
    application: TasApplicationModel;
    assistanceId?: string;
    record?: AppHistoryBase | PaymentEvent;
    logType?: string;
    tasParticipantActive?: boolean;
  };
  Element: HTMLDivElement;
}

class EditPaymentDropdown extends Component<EditPaymentDropDownSignature> {
  @tracked submitError = null;
  @tracked isEditModalOpen = false;
  @tracked reasonForEditPayable = '';
  @tracked editableAmount = 0;

  @tracked isMenuOpen = false;
  @tracked isCancelAssistanceModalOpen = false;
  @tracked assistanceAction = '';
  @tracked assistanceId = '';
  @tracked didSubmitEditRequest = false;

  @service declare store: StoreService;
  // @service declare notifications: NotificationsService;
  @service declare intl: Intl;
  @service declare tuitionAssistance: TuitionAssistanceService;

  constructor(owner: Owner, args: EditPaymentDropDownSignature['Args']) {
    super(owner, args);
    this.editableAmount =
      args.logType === 'approval'
        ? Number(args.record?.to ?? 0)
        : Math.abs(args.record?.amount ?? 0);
    this.assistanceId = args.assistanceId ?? '';
  }

  get showEditMenuItem() {
    return (
      // Using optional chaining so that if record is undefined it evaluates to undefined (which won't match)
      [
        'TAS.AssistanceState.PAYABLE',
        'TAS.AssistanceState.REPAYABLE',
        'TAS.AssistanceState.RESERVED',
      ].includes(this.args.record?.to ?? '') || this.args.logType === 'approval'
    );
  }

  updateReasonForEdit = (reason: string) => {
    this.reasonForEditPayable = reason;
  };

  updateAmount = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event);
    const amount = Math.round(Number(unmaskedValue) * 100);
    this.editableAmount = amount;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  handleMenuAction = (assistanceAction: string) => {
    this.assistanceAction = assistanceAction;
    this.isMenuOpen = false;
    setTimeout(() => {
      if (assistanceAction === 'delete') {
        this.openDeleteModal();
      } else {
        this.openEditModal();
      }
    }, 500);
  };

  setAction = (value: string) => {
    this.assistanceAction = value;
  };

  openDeleteModal = () => {
    this.isCancelAssistanceModalOpen = true;
  };

  closeCancelRepaymentModal = () => {
    this.isCancelAssistanceModalOpen = false;
    this.resetForm();
  };

  resetForm() {
    this.assistanceAction = '';
    this.reasonForEditPayable = '';
    this.editableAmount = 0;
    this.assistanceId = '';
    this.submitError = null;
  }

  openEditModal = () => {
    this.isEditModalOpen = true;
  };

  closeEditModal = () => {
    this.isEditModalOpen = false;
    this.resetForm();
  };

  get adapterMethod() {
    // Possible adapter methods based on assistanceAction (tas-application adapter):
    // - 'editApprovedTotal' → updateApprovedTotal(application, amount, reason)
    // - 'forgive' → forgiveRepayment(application, amount, reason)
    // - 'settle' → recordRepayment(application, amount, reason)
    // Note: 'edit' is handled separately via store.request(updateTasAssistanceAmountRequest(...))
    return actionMap[this.assistanceAction] || '';
  }

  submitTask = dropTask(async () => {
    this.submitError = null;
    this.didSubmitEditRequest = true;

    const isEditingAssistance = this.assistanceAction === 'edit';

    try {
      if (isEditingAssistance) {
        await this.store.request(
          updateTasAssistanceAmountRequest(
            this.assistanceId,
            this.editableAmount,
            this.reasonForEditPayable
          )
        );
      } else {
        const adapter = this.store.adapterFor('tas-application');
        // @ts-expect-error - Legacy adapter method, will be migrated
        await adapter[this.adapterMethod](
          this.args.application,
          this.editableAmount,
          this.reasonForEditPayable
        );
      }
      this.closeEditModal();
      this.didSubmitEditRequest = false;
      await this.args.application.loadHistory();
      await this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  cancelRepaymentTask = dropTask(async () => {
    this.submitError = null;
    this.didSubmitEditRequest = true;

    try {
      const adapter = this.store.adapterFor('tas-application');
      // @ts-expect-error - Legacy adapter method, will be migrated
      await adapter.cancelRepayment(this.args.application, this.reasonForEditPayable);
      this.closeCancelRepaymentModal();
      this.didSubmitEditRequest = false;
      await this.args.application.loadHistory();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  get isFormValid() {
    return this.reasonForEditPayable && this.editableAmount > 0 && this.assistanceAction;
  }

  get isDeleteFormValid() {
    return this.reasonForEditPayable && this.assistanceAction;
  }

  get shouldValidateAmountBasedOnLimits() {
    return !(this.assistanceAction === 'edit' || this.assistanceAction === 'editApprovedTotal');
  }

  get isSettleOrForgiveAction(): boolean {
    return this.assistanceAction === 'settle' || this.assistanceAction === 'forgive';
  }

  get amountDescriptionString(): string {
    const {
      assistanceAction,
      args: { record },
    } = this;

    const toState = record?.to ?? '';

    const descriptions: Record<string, string> = {
      settle:
        'Employee is paying back the employer. This can be 100% settlement or partial settlement.',
      forgive: 'Employee is being forgiven from the current repayment amount.',
      editApprovedTotal: 'By changing the amount, you are updating the approved total.',
    };

    if (assistanceAction === 'edit') {
      switch (toState) {
        case 'TAS.AssistanceState.REPAYABLE':
          return 'By changing the amount, you are updating the amount the employee owes the employer.';
        case 'TAS.AssistanceState.PAYABLE':
          return 'The amount you update below will affect the payment being paid out to the employee.';
        case 'TAS.AssistanceState.RESERVED':
          return 'By changing the amount, you are updating the reserved amount for this course cash payment.';
        default:
          return '';
      }
    }

    return descriptions[assistanceAction] ?? '';
  }

  <template>
    <Popover
      @placement="left"
      @maxWidth={{220}}
      @isOpen={{this.isMenuOpen}}
      @onClose={{this.closeMenu}}
    >
      <:trigger as |pop|>
        <button
          type="button"
          {{pop.register}}
          {{on "click" this.toggleMenu}}
          class="inline-flex items-center justify-center rounded border border-ocean-600 text-ocean-600 p-1 hover:bg-ocean-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ocean-600"
          data-test-trigger={{@assistanceId}}
          aria-label="Actions"
        >
          <PhDotsThreeVertical @size="24" />
        </button>
      </:trigger>
      <:body>
        <ul role="menu" class="py-1">
          {{#if this.showEditMenuItem}}
            <li role="none">
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                data-test-edit={{@assistanceId}}
                {{on
                  "click"
                  (fn
                    this.handleMenuAction (if (eq @logType "approval") "editApprovedTotal" "edit")
                  )
                }}
              >
                <PhNotePencil @size="16" />
                {{t "common.tas.approval.request_summary.change_amount"}}
              </button>
            </li>
          {{/if}}
          {{#if (eq @record.to "TAS.AssistanceState.REPAYABLE")}}
            <li role="none">
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                data-test-forgive={{@assistanceId}}
                {{on "click" (fn this.handleMenuAction "forgive")}}
              >
                <PhGift @size="16" />
                {{t "common.tas.approval.request_summary.forgive_label"}}
              </button>
            </li>
            <li role="none" class="border-t border-gray-100">
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                data-test-settle={{@assistanceId}}
                {{on "click" (fn this.handleMenuAction "settle")}}
              >
                <PhShieldCheck @size="16" />
                {{t "common.tas.approval.request_summary.mark_as_paid"}}
              </button>
            </li>
            <li role="none">
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                data-test-delete={{@assistanceId}}
                {{on "click" (fn this.handleMenuAction "delete")}}
              >
                <PhTrash @size="16" />
                {{t "common.tas.approval.request_summary.cancel_repayment"}}
              </button>
            </li>
          {{/if}}
        </ul>
      </:body>
    </Popover>

    <LimitsSummary
      @application={{@application}}
      @employee={{@application.tasProgramInstance.employee}}
      @approvedTotalToValidate={{this.editableAmount}}
      @ignoreLimitsValidation={{this.shouldValidateAmountBasedOnLimits}}
      @labelClass="w-full"
      @valueClass="w-full app-status-text-brand"
      as |Summary|
    >
      <Modal
        @isOpen={{this.isEditModalOpen}}
        @onClose={{this.closeEditModal}}
        data-test-edit-modal
        @ariaLabel={{t "common.aria_labels.update_request_details"}}
      >
        <:header>{{t "common.tas.approval.request_summary.update_request_details"}}</:header>
        <:body>
          {{#if this.isSettleOrForgiveAction}}
            <div>
              {{t "common.tas.approval.request_summary.select_action"}}
            </div>
            <div class="flex gap-4 mt-4 mb-8">
              <SelectableCard
                @value="settle"
                @label="Settle"
                @description="Employee is paying back the employer."
                @isSelected={{eq this.assistanceAction "settle"}}
                @onChange={{this.setAction}}
              />
              <SelectableCard
                @value="forgive"
                @label={{t "common.tas.approval.request_summary.forgive_label"}}
                @description="Employee is being forgiven from the repayment amount."
                @isSelected={{eq this.assistanceAction "forgive"}}
                @onChange={{this.setAction}}
              />
            </div>
          {{/if}}
          <form class="flex flex-col">
            <Input
              @label={{concat "Amount" "*"}}
              data-test-amount-update
              @value={{String (divide this.editableAmount 100)}}
              @onChange={{this.updateAmount}}
              @classes={{hash base="text-xs font-semibold"}}
              placeholder="$00.00"
              {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
            />

            <div>
              <p class="text-xs pt-1 px-2 text-tio-gray-400">
                {{this.amountDescriptionString}}
              </p>
              {{#unless this.isSettleOrForgiveAction}}
                <ReimbursementCalculationIfApplicable @application={{@application}} />
              {{/unless}}
              <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex" />
            </div>

            <Textarea
              data-test-reason-for-edit
              @value={{this.reasonForEditPayable}}
              @onChange={{this.updateReasonForEdit}}
              @label={{concat "Specify Reason" "*"}}
              @classes={{hash base="text-xs font-semibold mt-8"}}
              placeholder="Add a comment"
            />
            <ErrorMessages @showErrors={{this.didSubmitEditRequest}} @error={{this.submitError}} />
          </form>
          {{#if (and this.isSettleOrForgiveAction (not @tasParticipantActive))}}
            <div class="mt-4 text-sm">
              <ActionableTranslation
                @t="common.tuition_assistance.applications.forgive_dialog.held_warning"
                @links={{hash
                  goToEmployee=(hash
                    href=(concat
                      "/people/"
                      @application.tasProgramInstance.employee.person.id
                      "/tuition-assistance"
                    )
                    t="common.tuition_assistance.applications.forgive_dialog.employee_page_link"
                  )
                }}
              />
            </div>
          {{/if}}
        </:body>
        <:footer>
          <Footer
            @isRunning={{this.submitTask.isRunning}}
            @onCancel={{this.closeEditModal}}
            @onSubmit={{this.submitTask.perform}}
            @submitDisabled={{or (not this.isFormValid) Summary.isDisabledSubmitForApprover}}
          />
        </:footer>
      </Modal>
    </LimitsSummary>

    <Modal
      @isOpen={{this.isCancelAssistanceModalOpen}}
      @onClose={{this.closeCancelRepaymentModal}}
      data-test-delete-modal
      @ariaLabel={{t "common.aria_labels.delete_request"}}
    >
      <:header><div class="pr-2 text-pretty">{{t
            "common.tas.approval.request_summary.delete_request"
          }}</div></:header>
      <:body>
        <p>
          {{t "common.tas.approval.request_summary.affected_reimbursement"}}
        </p>

        <Textarea
          data-test-reason-for-delete
          @value={{this.reasonForEditPayable}}
          @onChange={{this.updateReasonForEdit}}
          @label={{concat (t "common.tas.approval.request_summary.specify_reason") "*"}}
          @classes={{hash base="text-xs font-semibold mt-8"}}
          placeholder={{t "common.tas.approval.request_summary.comment_placeholder"}}
        />
        <ErrorMessages @showErrors={{this.didSubmitEditRequest}} @error={{this.submitError}} />
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.cancelRepaymentTask.isRunning}}
          @onCancel={{this.closeCancelRepaymentModal}}
          @onSubmit={{this.cancelRepaymentTask.perform}}
          @cancelText={{t "common.cancel"}}
          @submitText={{t "common.delete"}}
          @submitDisabled={{not this.isDeleteFormValid}}
        />
      </:footer>
    </Modal>
  </template>
}
